import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let sid = localStorage.getItem("devisto_chat_session");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("devisto_chat_session", sid);
  }
  return sid;
};

const suggestionsByRole = {
  pro: ["Comment gérer mes demandes ?", "Comment envoyer un devis ?", "Comment fonctionne le Devisto Score ?"],
  property: ["Comment ajouter un immeuble ?", "Comment créer une demande pour un lot ?", "Comment suivre une intervention ?"],
  entreprise: ["Comment publier une mission ?", "Comment choisir un technicien ?", "Comment se passe le paiement ?"],
  default: ["Comment décrire mon projet ?", "Comment suivre ma demande ?", "Comment devenir partenaire pro ?"],
};

export const ChatWidget = () => {
  const { t, lang } = useLanguage();
  const { pathname } = useLocation();
  const inStudio = pathname.startsWith("/studio");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(typeof window !== "undefined" && window.scrollY > 240);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 240);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const role = localStorage.getItem("devisto_role") || "default";
  const suggestions = suggestionsByRole[role] || suggestionsByRole.default;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    const toggle = () => setOpen((v) => !v);
    window.addEventListener("devisto:toggle-chat", toggle);
    return () => window.removeEventListener("devisto:toggle-chat", toggle);
  }, []);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || sending) return;
    setInput("");
    const next = [...messages, { from: "user", text: message }];
    setMessages(next);
    setSending(true);
    try {
      const token = localStorage.getItem("devisto_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(
        `${API}/assistant/chat`,
        { session_id: getSessionId(), message, lang, history: next.slice(-10) },
        { headers, timeout: 60000 }
      );
      setMessages((m) => [...m, { from: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { from: "assistant", text: t("L'assistant n'a pas pu répondre. Réessayez.") }]);
    } finally {
      setSending(false);
    }
  };

  if (inStudio) return null;

  return (
    <>
      {open && (
        <div
          data-testid="chat-window"
          className="fixed bottom-40 lg:bottom-24 right-4 sm:right-6 w-[calc(100vw-2rem)] sm:w-[22rem] bg-white rounded-2xl shadow-2xl border border-[#E2E7F7] overflow-hidden z-50 flex flex-col"
          style={{ maxHeight: "min(32rem, calc(100vh - 8rem))" }}
        >
          <div className="bg-[#0F2645] text-white p-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <img src="/assets/devisto-wordmark-white.png" alt="Devisto" className="h-4 w-auto" />
              <span className="text-sm font-semibold">{t("Assistant Devisto")}</span>
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-300">
                <span className="w-2 h-2 rounded-full bg-[#10B981]" />
                {t("En ligne")}
              </span>
            </div>
            <button
              type="button"
              data-testid="chat-close-btn"
              aria-label={t("Fermer")}
              onClick={() => setOpen(false)}
              className="text-slate-300 hover:text-white transition-colors duration-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div data-testid="chat-messages" className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F6F8FD] min-h-[16rem]">
            <div className="max-w-[85%] rounded-2xl rounded-tl-md bg-white border border-[#E2E7F7] px-4 py-2.5 text-sm text-[#0F2645] leading-relaxed">
              {t("Bonjour, je suis l'assistant Devisto. Comment puis-je vous aider ?")}
            </div>
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={m.from === "user" ? `chat-msg-user-${i}` : `chat-msg-assistant-${i}`}
                className={
                  m.from === "user"
                    ? "ml-auto max-w-[85%] rounded-2xl rounded-tr-md bg-[#3A5CC8] px-4 py-2.5 text-sm text-white leading-relaxed"
                    : "max-w-[85%] rounded-2xl rounded-tl-md bg-white border border-[#E2E7F7] px-4 py-2.5 text-sm text-[#0F2645] leading-relaxed whitespace-pre-wrap"
                }
              >
                {m.text}
              </div>
            ))}
            {sending && (
              <div data-testid="chat-typing" className="max-w-[85%] rounded-2xl rounded-tl-md bg-white border border-[#E2E7F7] px-4 py-2.5">
                <Loader2 className="w-4 h-4 animate-spin text-[#3A5CC8]" />
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {messages.length === 0 && (
            <div className="px-4 pb-2 pt-1 bg-[#F6F8FD] flex flex-wrap gap-2 shrink-0">
              {suggestions.map((s, i) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`chat-suggestion-${i}`}
                  onClick={() => send(s)}
                  className="rounded-full border border-[#3A5CC8]/30 bg-white px-3 py-1.5 text-xs font-medium text-[#3A5CC8] hover:bg-[#EEF1FC] transition-colors duration-200"
                >
                  {t(s)}
                </button>
              ))}
            </div>
          )}

          <form
            className="p-3 border-t border-[#E2E7F7] bg-white flex items-center gap-2 shrink-0"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("Posez votre question…")}
              maxLength={1000}
              className="flex-1 rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] px-4 py-2.5 text-sm text-slate-800 placeholder:text-[#93A0BC] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8] focus:border-blue-500"
            />
            <button
              type="submit"
              data-testid="chat-send-btn"
              disabled={sending || !input.trim()}
              aria-label={t("Envoyer")}
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[#3A5CC8] text-white hover:bg-[#3A5CC8] disabled:opacity-40 disabled:pointer-events-none transition-colors duration-200 shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}

      <button
        type="button"
        data-testid="chat-widget-toggle"
        aria-label={t("Ouvrir le chat")}
        onClick={() => setOpen(!open)}
        className={`fixed bottom-[5.5rem] lg:bottom-6 right-4 sm:right-6 w-14 h-14 bg-[#3A5CC8] text-white rounded-full shadow-lg shadow-[#3A5CC8]/35 ${inStudio ? "hidden lg:flex" : pathname === "/" && !scrolled && !open ? "hidden lg:flex" : "flex"} items-center justify-center hover:scale-105 transition-transform duration-200 z-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]`}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
};
